<template>
  <div class="contact-phone">
    <StepHeader
      :title="title"
      :theme="theme"
    />

    <StepContent
      width="sm"
      :theme="theme"
    >
      <div
        v-if="!twilioFailed"
        class="contact-phone__content w-full max-w-500 m-auto"
      >
        <p>
          We will call you as soon as you complete your application or during the next business day
          if completed outside of normal business hours.
          <br>
        </p>
        <p class="font-semibold">
          When you tap “Continue”, Spring Financial will send a text with a verification code.
          Message and data rates may apply.
        </p>

        <AppInputWithValidation
          v-for="(field, index) in fields"
          :id="field.name"
          :key="index"
          :label="field.label"
          :name="field.name"
          :type="field.type"
          :mask="field.mask"
          :value="field.value"
          :rules="field.rules"
          :error="phoneNumberError"
          :placeholder-char="field.placeholderChar"
          :placeholder="field.placeholder"
          :hide-error="!submitted"
          :pipe="filterPhoneNumberFunction"
          :theme="theme"
          @input="$_input_updateState(field.vuexAction, $event)"
        >
          <template #countryBox>
            <div class="country-box" />
          </template>
        </AppInputWithValidation>

        <AppRadio
          v-for="input of inputs"
          :id="input.id"
          :key="input.id"
          :name="input.name"
          :label="input.label"
          :value="input.value"
          class="mt-10 mx-auto mb-30"
          :theme="theme"
          :checked="messageChannel === input.value"
          @input="messageChannelSelected"
        />

        <AppCheckbox
          v-if="checkbox"
          theme="black"
          :value="checkboxValue"
          :square="true"
          @input="handleCheckbox($event)"
        >
          <div class="text-xs">
            Receive promotions and updates from Spring Financial.
            It’s free and you can opt-out at any time.
            By clicking “continue" I agree to the
            <a
              :class="textColorClass()"
              :href="generalTermsOfServiceUrl"
              target="_blank"
            >
              terms and conditions
            </a> and
            <a
              :class="textColorClass()"
              :href="privacyPolicyUrl"
              target="_blank"
            >
              privacy policy
            </a>,
            and for Spring Financial to use my credit information provided by a Credit Rating Agency to qualify me for potential services. I am aware that this will not impact my credit score.
            <br>
          </div>
        </AppCheckbox>
      </div>

      <ContactFailure
        v-else-if="twilioFailed"
        :theme="theme"
      />

      <div
        v-if="mockEZBotCall"
        class="contact-phone__ez-mock"
      >
        <select
          class="border"
          @change="setMockEzbotMessage($event)"
        >
          <option
            v-for="(item, index) in mockEZBotValues"
            :key="index"
            :value="item"
          >
            {{ item }}
          </option>
        </select>
      </div>

      <div
        v-if="mockProgressaCall"
        class="contact-phone__ez-mock"
      >
        <select
          class="border"
          @change="setMockProgressaMessage($event)"
        >
          <option
            v-for="(item, index) in mockProgressaValues"
            :key="index"
            :value="item"
          >
            {{ item }}
          </option>
        </select>
      </div>
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="callingTwilio"
        @click="handleContinue"
      >
        Continue
      </AppButton>
    </StepFooter>
  </div>
</template>

<script>
import AppRadio from '@/components/common/inputs/AppRadio'
import AppButton from '@/components/common/buttons/AppButton'
import AppInputWithValidation from '@/components/common/inputs/AppInputWithValidation'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepHeader from '@/components/common/step-layout/StepHeader'
import ContactFailure from '@/components/common/notifications/ContactFailure'
import AppCheckbox from '@/components/common/inputs/AppCheckbox'
import input from '@/mixins/input'
import navigation from '@/mixins/navigation'
import buttonList from '@/mixins/button-list'
import submitLoans from '@/mixins/submit/submit-loans'
import DataLayer from '@/utilities/data-layer'
import theme from '@/mixins/theme'
import submitMortgage from '@/mixins/submit/submit-mortgage'
import step from '@/mixins/step'

export default {
  name: 'ContactPhone',

  // Components
  components: {
    AppRadio,
    AppButton,
    AppInputWithValidation,
    StepContent,
    StepFooter,
    StepHeader,
    ContactFailure,
    AppCheckbox
  },

  // Mixins
  mixins: [
    input,
    buttonList,
    navigation,
    submitLoans,
    theme,
    submitMortgage,
    step
  ],

  // Props
  props: {
    title: {
      type: String,
      default: 'How Can We Reach You?'
    },

    theme: {
      type: String,
      default: 'blue'
    },

    checkbox: {
      type: Boolean,
      default: false
    }
  },

  // Data
  data () {
    return {
      messageChannel: 'sms',
      callingTwilio: false,
      submitted: false,
      phoneInputMask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      inputs: [{
        id: 'sms-code',
        name: 'sms-code',
        value: 'sms',
        label: 'Send me a code by text message.',
        ref: 'sms-code',
        default: true
      }, {
        id: 'call-code',
        name: 'call-code',
        value: 'call',
        label: 'Call me with a code.',
        ref: 'call-code'
      }],
      checkboxValue: false,
      mockEZBotCall: false,
      mockEZBotValues: [
        '',
        'error:existing easyfinancial customer. application cannot proceed.',
        'FAIL',
        'PASS',
        'Error',
        'error:have a co-applicant: you must enter a value',
        'error:applicant must not be on social-assistance',
        'CAUTION'
      ],
      mockEzBotSelectedValue: '',
      mockProgressaCall: false,
      mockProgressaValues: [
        '',
        'Prequalified',
        'Declined'
      ],
      mockProgressaSelectedValue: ''
    }
  },

  // Computed
  computed: {

    phoneNumberError () {
      return this.$store.state.validations.phoneNumberError
    },

    homePhone () {
      return this.$store.state.customer.home_phone
    },

    fields () {
      return [{
        label: 'Phone Number',
        name: 'phone',
        type: 'tel',
        value: this.homePhone,
        mask: this.phoneInputMask,
        rules: 'required:Phone Number|phone',
        vuexAction: 'setHomePhone',
        placeholderChar: '_',
        placeholder: '(___)___-____'
      }]
    },

    dataLayerObject () {
      return {
        event: `spring_${this.eventPrefix}_phone_number_continue`,
        verification_type: this.messageChannel
      }
    },

    generalTermsOfServiceUrl () {
      return `${process.env.VUE_APP_MINISITE_URL}general-terms-of-service`
    },

    privacyPolicyUrl () {
      return `${process.env.VUE_APP_MINISITE_URL}privacy-policy`
    },

    twilioFailed () {
      return this.$store.state.application.twilioFailed
    },

    isEzBotEnabled () {
      return process.env.VUE_APP_EZ_BOT_ENABLED
    },

    isEzBotTriggered () {
      return this.$store.state.application.isEzBotTriggered
    },

    isDeeperPrepop () {
      return this.$store.state.application.isDeeperPrepop
    }
  },

  // Watch
  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    }
  },

  created () {
    const mockEZBot = process.env.VUE_APP_FORCE_MOCK_EZ_BOT
    if (mockEZBot) window.addEventListener(mockEZBot, this.handleForceEZBotMock)
    const mockProgressa = process.env.VUE_APP_FORCE_MOCK_PROGRESSA
    if (mockProgressa) {
      window.addEventListener(mockProgressa, this.handleForceProgressaMock)
    }
  },

  // Methods
  methods: {
    messageChannelSelected (messageChannel) {
      this.messageChannel = messageChannel
      const channelEvent = this.messageChannel === 'sms'
        ? 'verification_type_text'
        : 'verification_type_call'
      // push to DL if it doesn't exist yet
      if (!dataLayer.some(dl => dl['verification_type_selected'] === channelEvent)) {
        DataLayer.pushObject({ verification_type_selected: channelEvent })
      }
    },

    // Function to deny + or 1 from being enteres as the first character
    filterPhoneNumberFunction (conformedValue) {
      if (conformedValue.charAt(1) === '1' || conformedValue.charAt(1) === '+') {
        return false
      } else {
        return conformedValue
      }
    },

    // twilio request
    async handleContinue () {
      const isValid = await this.$_navigation_validateStep()

      if (isValid && !this.isDeeperPrepop && !this.$_navigation_isMortgage) this.triggerEzbot()
      if (this.mockProgressaSelectedValue) this.$_trigger_mockProgressa(this.mockProgressaSelectedValue)
      this.submitted = true
      if (this.checkbox) this.$store.dispatch('setConsentCEM', this.checkboxValue)
      if (this.callingTwilio) return false
      if (this.twilioFailed) {
        DataLayer.pushObject({ 'verification_successful': 0 })
        if (this.$_navigation_isMortgage) await this.$_submit_mortgageLead()
        this.$_step_handleContinue({
          Twilio_Phone_Verified__c: false,
          home_phone: this.homePhone
        })
      } else {
        if (!isValid) return
        if (!this.isDeeperPrepop && !this.$_navigation_isMortgage) this.$_loans_updateLead({ home_phone: this.homePhone }, this.dataLayerObject)
        if (this.eventPrefix === 'ploan') {
          window.rudderanalytics.track(`spring_ploan_phone_number_continue`)
        }
        this.sendTwilioRequest()
        if (this.isDeeperPrepop || this.$_navigation_isMortgage) {
          // go next step
          this.$_navigation_handleNext()
        }
      }
    },

    async sendTwilioRequest () {
      this.$store.dispatch('setVerificationMethod', this.messageChannel)
      try {
        this.callingTwilio = true
        // call lambda middleware to check if phone number
        // is valid and can receive a verificationSid
        await this.$store.dispatch('requestVerification', {
          phone: this.homePhone.replace(/[^0-9]/g, ''),
          channel: this.messageChannel
        })
      } catch (error) {
        // twilio rejected the phone number
        // render ContactFailure component if Twilio/our service
        // is down to avoid losing a potential valid lead
        this.$store.dispatch('setTwilioFailed', true)
      } finally {
        this.callingTwilio = false
      }
    },

    handleCheckbox (val) {
      this.checkboxValue = val
    },

    triggerEzbot () {
      if (this.isEzBotEnabled && !this.isEzBotTriggered) {
        this.$store.dispatch('setEzBotTriggered', true)
        this.$_trigger_ezbot(this.homePhone.replace(/[^0-9]/g, ''), this.mockEzBotSelectedValue)
      }
    },

    handleForceEZBotMock () {
      this.mockEZBotCall = true
    },

    setMockEzbotMessage (event) {
      this.mockEzBotSelectedValue = event.target.value
    },

    handleForceProgressaMock () {
      this.mockProgressaCall = true
    },

    setMockProgressaMessage (event) {
      this.mockProgressaSelectedValue = event.target.value
    }
  }
}
</script>

<style scoped lang='scss'>
.contact-phone {
  &__content {
    p {
      @apply text-base max-w-500 mb-20;
    }
  }

  &__ez-mock {
    @apply flex justify-center;
  }
}
</style>
